import type { HaulierCreatePayload } from "@/customTypings/haulier/Haulier";
import { authHeader, handleResponse } from "..";

export const HAULIER_CREATE_CONFLICT = {
    duplicateName: "haulier.duplicateName",
    duplicateLicence: "haulier.duplicateLicence",
    deletedRestoreAvailable: "haulier.deletedRestoreAvailable",
    duplicateAmbiguous: "haulier.duplicateAmbiguous",
} as const;

export type HaulierDeletedRestoreAvailableConflict = {
    code: string;
    message: string;
    existingId: string;
    existingName: string;
};

export function isHaulierDeletedRestoreAvailableConflict(
    value: unknown,
    expectedCode: string
): value is HaulierDeletedRestoreAvailableConflict {
    return (
        typeof value === "object" &&
        value !== null &&
        (value as HaulierDeletedRestoreAvailableConflict).code === expectedCode &&
        typeof (value as HaulierDeletedRestoreAvailableConflict).existingId === "string"
    );
}

export class HaulierCreateConflictError extends Error {
    readonly conflict: HaulierDeletedRestoreAvailableConflict;

    constructor(conflict: HaulierDeletedRestoreAvailableConflict) {
        super(conflict.message);
        this.name = "HaulierCreateConflictError";
        this.conflict = conflict;
    }
}

export type HaulierRestoreDeletedPayload = HaulierCreatePayload & { id: string };

function siteHauliersBase(siteId: string) {
    return `/api/sites/${encodeURIComponent(siteId)}/hauliers`;
}

export async function postHaulierCreate<TDetail>(
    siteId: string,
    payload: HaulierCreatePayload
): Promise<TDetail> {
    const response = await fetch(`${siteHauliersBase(siteId)}/create`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    });
    const text = await response.text();
    let data: unknown = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        /* non-JSON */
    }

    if (
        response.status === 409 &&
        isHaulierDeletedRestoreAvailableConflict(data, HAULIER_CREATE_CONFLICT.deletedRestoreAvailable)
    ) {
        throw new HaulierCreateConflictError(data);
    }

    if (!response.ok) {
        const err = data as { code?: string; message?: string } | null;
        if (err?.code === HAULIER_CREATE_CONFLICT.duplicateName) {
            throw new Error(err.message ?? "A Haulier with this name already exists at this site.");
        }
        if (err?.code === HAULIER_CREATE_CONFLICT.duplicateLicence) {
            throw new Error(err.message ?? "A Haulier with this carrier licence already exists at this site.");
        }
        if (err?.code === HAULIER_CREATE_CONFLICT.duplicateAmbiguous) {
            throw new Error(err.message ?? "Multiple deleted Hauliers match these details.");
        }
    }

    return handleResponse({
        text: async () => text,
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
    }) as Promise<TDetail>;
}
